/**
 * Context React pour gérer l'état du panier e-commerce
 * Permet de partager le panier entre Shop et Checkout
 */

import { createContext, useContext, useState, ReactNode } from 'react';
import type { Product } from '../types/pos.types';
import { logger } from '../utils/logger';

export interface CartItem {
  product: Product;
  quantity: number;
}

interface CartContextType {
  cart: CartItem[];
  cartOpen: boolean;
  addToCart: (product: Product) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  removeFromCart: (productId: string) => void;
  clearCart: () => void;
  setCartOpen: (open: boolean) => void;
  cartTotal: number;
  cartCount: number;

  // Panier hybride - séparation items normaux vs précommandes
  regularItems: CartItem[]; // Produits à livrer (fruits/légumes)
  preorderItems: CartItem[]; // Produits en précommande (pains)
  regularTotal: number; // Total livraison
  preorderTotal: number; // Total précommande
  hasPreorderItems: boolean; // true si le panier contient des précommandes
  hasRegularItems: boolean; // true si le panier contient des items normaux
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);

  const addToCart = (product: Product) => {
    logger.log('[CartContext] Adding product to cart:', product.name, 'preorder_enabled:', product.preorder_enabled);
    const existingItem = cart.find((item) => item.product.id === product.id);
    if (existingItem) {
      setCart(
        cart.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setCart([...cart, { product, quantity: 1 }]);
    }
    setCartOpen(true);
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      setCart(cart.filter((item) => item.product.id !== productId));
    } else {
      setCart(
        cart.map((item) =>
          item.product.id === productId ? { ...item, quantity } : item
        )
      );
    }
  };

  const removeFromCart = (productId: string) => {
    setCart(cart.filter((item) => item.product.id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    setCartOpen(false);
  };

  // Calculer les totaux
  const cartTotal = cart.reduce((total, item) => {
    const price = item.product.prices?.[0]?.amount_cents ?? 0;
    return total + price * item.quantity;
  }, 0);

  const cartCount = cart.reduce((count, item) => count + item.quantity, 0);

  // Identifier si le panier contient des produits avec précommande (pour info uniquement)
  const hasPreorderItems = cart.some(item => item.product.preorder_enabled);
  const hasRegularItems = cart.some(item => !item.product.preorder_enabled);

  logger.log('[CartContext] Cart items:', cart.length, 'hasPreorderItems:', hasPreorderItems);
  cart.forEach(item => {
    logger.log('[CartContext] - Item:', item.product.name, 'preorder_enabled:', item.product.preorder_enabled);
  });

  // Pour compatibilité avec l'ancien code, on retourne tous les items comme "regular"
  const regularItems = cart;
  const preorderItems: CartItem[] = [];
  const regularTotal = cartTotal;
  const preorderTotal = 0;

  return (
    <CartContext.Provider
      value={{
        cart,
        cartOpen,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        setCartOpen,
        cartTotal,
        cartCount,
        regularItems,
        preorderItems,
        regularTotal,
        preorderTotal,
        hasPreorderItems,
        hasRegularItems,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
